import { useState } from 'react';
import { supabase } from '@/lib/supabase';

export default function DebugAuth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [result, setResult] = useState<string>('');
  const [loading, setLoading] = useState(false);

  async function runCheck() {
    setLoading(true);
    setResult('');
    try {
      // Step 1: try login
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInError) {
        setResult('Auth FAILED: ' + signInError.message);
        setLoading(false);
        return;
      }

      const userId = signInData.user?.id ?? '';
      let output = `Auth OK\n  user_id: ${userId}\n  email: ${signInData.user?.email}\n\n`;

      // Step 2: profile by id
      const { data: byId, error: byIdErr } = await supabase
        .from('jphph_profiles')
        .select('id, email, user_type, is_active')
        .eq('id', userId)
        .maybeSingle();

      output += `Profile by id (${userId}):\n  result: ${JSON.stringify(byId)}\n  error: ${byIdErr?.message || 'none'}\n\n`;

      // Step 3: profile by email (diagnostic for id mismatch)
      const { data: byEmail, error: byEmailErr } = await supabase
        .from('jphph_profiles')
        .select('id, email, user_type, is_active')
        .eq('email', email.trim().toLowerCase())
        .maybeSingle();

      output += `Profile by email (${email}):\n  result: ${JSON.stringify(byEmail)}\n  error: ${byEmailErr?.message || 'none'}\n\n`;

      if (byId) {
        output += `CONCLUSION: Profile found by id. user_type=${byId.user_type}. Login should work.\n`;
      } else if (byEmail) {
        output += `CONCLUSION: ID MISMATCH!\n  jphph_profiles.id = ${byEmail.id}\n  auth.user.id = ${userId}\n  Fix: update jphph_profiles row id to match auth user id, or delete the old row and create a new one.\n`;
      } else {
        output += `CONCLUSION: No profile found at all.\n  Create a row in jphph_profiles with id = ${userId}, email = ${email}, user_type = management|subcontractor|client, is_active = true\n`;
      }

      setResult(output);
    } catch (err) {
      setResult('Exception: ' + String(err));
    } finally {
      setLoading(false);
    }
  }

  async function resetSession() {
    await supabase.auth.signOut();
    // Also clear any stale tokens from localStorage
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.includes('auth-token')) {
        localStorage.removeItem(key);
      }
    }
    setResult('Session reset. All Supabase auth tokens cleared. Please try logging in again.');
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-[640px] bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h1 className="text-xl font-bold text-gray-900">Auth Diagnostics</h1>
        <p className="text-sm text-gray-500">
          Use this to test login and profile lookup. Check browser console for more logs.
        </p>

        <div className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={runCheck}
            disabled={loading}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700 disabled:opacity-50"
          >
            {loading ? 'Checking...' : 'Run Check'}
          </button>
          <button
            onClick={resetSession}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm font-semibold hover:bg-gray-300"
          >
            Reset Session
          </button>
        </div>

        {result && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{result}</pre>
          </div>
        )}

        <div className="mt-4 space-y-2">
          <h2 className="text-sm font-semibold text-gray-800">Session Info</h2>
          <SessionInfo />
        </div>
      </div>
    </div>
  );
}

function SessionInfo() {
  const [info, setInfo] = useState<string>('Loading...');

  useState(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        setInfo('No active session');
        return;
      }
      setInfo(
        `user.id: ${session.user.id}\n` +
        `user.email: ${session.user.email}\n` +
        `expires_at: ${session.expires_at}\n` +
        `token length: ${session.access_token.length} chars`
      );
    });
  });

  return <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">{info}</pre>;
}